import {ProductItem} from "models/Product"
import logger from "@next/core-logger"
import breakpointToViewportSize from "../../utils/breakpointToViewportSize"
import type State from "../../models/State"
import {FacetsState} from "../../models/FacetsState"
import {ItemsPerPage} from "../../models/ItemsPerPage"
import {MapProductsFragmentJsonToItemsResponse, MapProductsFragmentToItemsResponse} from "../../utils/productsFragment"
import {SearchState, TemplateOverride} from "../../models/SearchState"
import {getScrollPosition} from "../../utils/urlBuilder"
import {TemplateSettingsDuckState, TemplateSettingsModel} from "../../models/templates"
import {TEMPLATE_OVERRIDE_INCLUDE_COMPONENTS} from "../../config/constants"
import {SearchApiResponse} from "../../models/searchApi"
import {STANDARD_TEMPLATE} from "../../templating/constants"

export const applyIncompatibleWithToFacets = <T extends {facets: FacetsState}>(stateCopy: T, value: string) => {
    const disableElements = stateCopy.facets[value]?.incompatibleWith
    if (disableElements?.length) {
        disableElements.forEach(option => {
            if (stateCopy.facets[option]) {
                // eslint-disable-next-line no-param-reassign
                stateCopy.facets[option] = {...stateCopy.facets[option], d: !stateCopy.facets[option].d}
            }
        })
    }
    return stateCopy
}

export const calculateStartItem = (
    pageToFetch: number,
    initialItemsPerPage: number,
    subsequentItemsPerPage: number,
) => {
    const currentSubsequentPageAmt = pageToFetch - 1
    if (pageToFetch === 2) return initialItemsPerPage
    const subsidiseItems = subsequentItemsPerPage > initialItemsPerPage
    return subsidiseItems
        ? (currentSubsequentPageAmt - 1) * subsequentItemsPerPage
        : currentSubsequentPageAmt * subsequentItemsPerPage
}

export const getLazyloadItemIndexFrom = (state: State) => {
    const itemsPerRow = selectItemsPerRow(state)
    const subsequentPagesNonLazyloadRows = selectSubsequentPagesNonLazyloadRows(state)
    return subsequentPagesNonLazyloadRows * itemsPerRow
}

export const selectSubsequentPagesNonLazyloadRows = (state: State) => {
    const {subsequentPagesNonLazyloadRows, currentBreakpoint} = state.search
    return subsequentPagesNonLazyloadRows[currentBreakpoint] as number
}

export const selectSubsequentItemsPerPage = (state: State) => {
    const {itemsPerPage, currentBreakpoint} = state.search
    return itemsPerPage.subsequent[breakpointToViewportSize(currentBreakpoint)] as number
}

export const selectInitialItemsPerPage = (state: State) => {
    const {itemsPerPage, currentBreakpoint} = state.search
    return itemsPerPage.initial[breakpointToViewportSize(currentBreakpoint)] as number
}
export const initialAndSubsequentPageSizeEqual = (state: State) => {
    const initialItemsPerPage = selectInitialItemsPerPage(state)
    const subsequentItemsPerPage = selectSubsequentItemsPerPage(state)
    return initialItemsPerPage === subsequentItemsPerPage
}
export const selectFetchTriggerOffset = (state: State) => {
    const itemsPerRow = selectItemsPerRow(state)
    const offsetRows = selectFetchTriggerOffsetRows(state)
    return offsetRows * itemsPerRow
}

export const selectItemsPerRow = (state: State) => {
    const {itemsPerRow, currentBreakpoint} = state.search
    return itemsPerRow[currentBreakpoint] as number
}

export const selectFetchTriggerOffsetRows = (state: State) => {
    const {fetchTriggerOffsetRows, currentBreakpoint} = state.search
    return fetchTriggerOffsetRows[currentBreakpoint] as number
}

export const getInitialPage = (itemsPerPage: ItemsPerPage, currentBreakpoint: string, historyUrl?: string) => {
    let startPage = 1
    let endPage = 1
    let scrollLocation = 0
    let pageSize: number = itemsPerPage.initial[breakpointToViewportSize(currentBreakpoint)]

    if (historyUrl) {
        const url = new URL(historyUrl)
        const {searchParams} = url
        // eslint-disable-next-line dot-notation
        const pageNumberFromUri = parseInt(searchParams.get("p") ?? "0", 10)
        if (pageNumberFromUri) {
            scrollLocation = getScrollPosition(historyUrl)
            endPage = pageNumberFromUri
            if (endPage > 2) {
                startPage = endPage - 1
            }

            if (startPage > 1) {
                pageSize = itemsPerPage.subsequent[breakpointToViewportSize(currentBreakpoint)]
            }
        }
    }

    return {
        startPage,
        endPage,
        pageSize,
        scrollLocation,
    }
}

/**
 * When no products are returned from the search, we want to persist the previous filter/facet selection
 * so the user can amend their search if they want to.
 */
export const formatSearchState = (
    previousSearchState: SearchState,
    decodedApiResponse: MapProductsFragmentJsonToItemsResponse | MapProductsFragmentToItemsResponse,
    items: ProductItem[],
    pageSize: number,
    itemsCacheSize: number,
): SearchState => {
    const actualCacheSize = itemsCacheSize - pageSize
    if (decodedApiResponse.searchStateData?.totalResults === 0) {
        return {
            ...previousSearchState,
            facets: previousSearchState.facets,
            filters: previousSearchState.filters,
            filtersSort: previousSearchState.filtersSort,
            totalResults: decodedApiResponse.searchStateData.totalResults,
            sorting: decodedApiResponse.searchStateData.sorting,
            items: [...decodedApiResponse.items, ...items.slice(pageSize, pageSize + actualCacheSize)],
            title: decodedApiResponse.searchStateData.title,
            startPage: 1,
            endPage: 1,
            searchBannerHtml: decodedApiResponse.searchBannerHtml,
            searchBannerJson: decodedApiResponse.searchBannerJson,
            searchSource: decodedApiResponse.searchStateData.searchSource,
        }
    }
    return {
        ...previousSearchState,
        facets: (decodedApiResponse.searchStateData as any).facets,
        filters: (decodedApiResponse.searchStateData as any).filters,
        filtersSort: (decodedApiResponse.searchStateData as any).filtersSort,
        totalResults: (decodedApiResponse.searchStateData as any).totalResults,
        sorting: (decodedApiResponse.searchStateData as any).sorting,
        items: [...decodedApiResponse.items, ...items.slice(pageSize, pageSize + actualCacheSize)],
        title: (decodedApiResponse.searchStateData as any).title,
        startPage: 1,
        endPage: 1,
        searchBannerHtml: decodedApiResponse.searchBannerHtml,
        searchBannerJson: decodedApiResponse.searchBannerJson,
        searchSource: (decodedApiResponse.searchStateData as any).searchSource,
    }
}

export const extractTemplateOverride = (
    response: Partial<SearchApiResponse>,
    previousTemplate: TemplateSettingsModel,
    validTemplates: TemplateSettingsDuckState = {},
    appTemplate: string,
    dispatchAction: (mainTemplate: string, fallbackTemplate: string) => void,
) => {
    let mainTemplate = previousTemplate?.mainTemplate
    let fallbackTemplate = previousTemplate?.fallbackTemplate

    if (response?.includedComponents?.includes(TEMPLATE_OVERRIDE_INCLUDE_COMPONENTS)) {
        const templateName = response?.templateOverride?.[appTemplate] as string
        const identifiedTemplate = validTemplates[templateName]

        if (identifiedTemplate) {
            mainTemplate = templateName
            fallbackTemplate = identifiedTemplate?.fallback || STANDARD_TEMPLATE
        } else {
            logger.warn(
                `${appTemplate} - ${templateName} from search api response not valid. Falling back to ${mainTemplate}`,
            )
        }

        dispatchAction(mainTemplate, fallbackTemplate)
    }
}

export function generateItemsString(itemsArray: ProductItem[], templateOverride: TemplateOverride = {}) {
    return itemsArray.reduce((acc, item, index) => {
        const productSummaryTemplateName = templateOverride?.productSummaryTemplateName
            ? `|${templateOverride.productSummaryTemplateName}`
            : `|`
        const imageRatio = templateOverride?.imageRatio ? `|${templateOverride.imageRatio}` : `|`
        return `${acc}${item.type}|${item.itemNumber}|${item.newIn}${productSummaryTemplateName}${imageRatio}${
            item.title
        }${index === itemsArray.length - 1 ? "" : ","}`
    }, "")
}
